import type { DataPoint } from "./series.js";

// Summary stat pills for a series (CHARTS_REDESIGN_PLAN.md §8).
// Min / Max / Avg / Latest, plus Δ vs the immediately preceding equal-length
// window. Δ styling is neutral (gray) — no red/green health judgments.

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function avg(points: DataPoint[]): number {
  return points.reduce((s, p) => s + p.y, 0) / points.length;
}

export function StatBadges({
  points,
  unit,
  prevPoints,
}: {
  points: DataPoint[];
  unit?: string;
  prevPoints?: DataPoint[];
}) {
  if (points.length === 0) return null;

  const ys = points.map((p) => p.y);
  const unitSuffix = unit && !unit.startsWith("/") ? ` ${unit}` : unit ?? "";

  let delta: { dir: "up" | "down" | "flat"; pct: number } | null = null;
  if (prevPoints && prevPoints.length > 0) {
    const prevAvg = avg(prevPoints);
    if (prevAvg !== 0) {
      const pct = ((avg(points) - prevAvg) / Math.abs(prevAvg)) * 100;
      delta = {
        dir: Math.abs(pct) < 0.5 ? "flat" : pct > 0 ? "up" : "down",
        pct: Math.abs(pct),
      };
    }
  }

  const Badge = ({ label, value }: { label: string; value: string }) => (
    <span className="inline-flex items-baseline gap-1 rounded-md bg-gray-100 px-2 py-1 dark:bg-gray-800">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
    </span>
  );

  return (
    <div className="flex flex-wrap gap-1.5 text-xs sm:text-sm">
      <Badge label="Min" value={`${fmt(Math.min(...ys))}${unitSuffix}`} />
      <Badge label="Max" value={`${fmt(Math.max(...ys))}${unitSuffix}`} />
      <Badge label="Avg" value={`${fmt(avg(points))}${unitSuffix}`} />
      <Badge label="Latest" value={`${fmt(ys[ys.length - 1])}${unitSuffix}`} />
      {delta && (
        <span className="inline-flex items-baseline gap-1 rounded-md bg-gray-100 px-2 py-1 dark:bg-gray-800">
          <span className="text-gray-500 dark:text-gray-400">vs prev</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {delta.dir === "flat" ? "–" : `${delta.dir === "up" ? "▲" : "▼"} ${fmt(delta.pct)}%`}
          </span>
        </span>
      )}
    </div>
  );
}
